/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { SiVisa, SiMastercard, SiAmericanexpress, SiDiscover, SiApplepay } from '@icons-pack/react-simple-icons';
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';

interface PaymentBadgesProps {
    /** Page Designer content-slot promise (pageId "footer-payment-badges"). Undefined outside the app loader (e.g. Storybook/tests) falls back to the static badges directly. */
    page?: Promise<PageWithComponentData | null>;
}

const PAYMENT_ICONS = [
    { Icon: SiVisa, labelKey: 'paymentMethods.visaLabel' },
    { Icon: SiMastercard, labelKey: 'paymentMethods.mastercardLabel' },
    { Icon: SiAmericanexpress, labelKey: 'paymentMethods.amexLabel' },
    { Icon: SiDiscover, labelKey: 'paymentMethods.discoverLabel' },
    { Icon: SiApplepay, labelKey: 'paymentMethods.applePayLabel' },
] as const;

/**
 * Mock/fallback payment badges — rendered when no Business-Manager-authored
 * content exists yet for the "footer-payment-badges" content-slot page.
 */
function StaticPaymentBadges(): ReactElement {
    const { t } = useTranslation('footer');
    return (
        <div className="flex flex-wrap items-center gap-3" data-testid="static-payment-badges">
            {PAYMENT_ICONS.map(({ Icon, labelKey }) => (
                <Icon key={labelKey} className="w-8 h-8 text-muted-foreground" title={t(labelKey)} />
            ))}
        </div>
    );
}

export default function PaymentBadges({ page }: PaymentBadgesProps): ReactElement {
    if (!page) {
        return <StaticPaymentBadges />;
    }

    return <Region page={page} regionId="content" errorElement={<StaticPaymentBadges />} />;
}
